import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/impressum')({
  component: ImpressumPage,
});

function ImpressumPage() {
  return (
    <>
      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroSlideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes gradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .legal-hero-bg {
          background: linear-gradient(160deg, #1d1d1b 0%, #2c1a19 50%, #1d1d1b 100%);
          background-size: 200% 200%;
          animation: gradientShift 14s ease infinite;
        }
        .legal-eyebrow {
          animation: heroSlideIn 0.6s ease-out forwards;
          opacity: 0;
        }
        .legal-title {
          animation: heroFadeUp 0.7s ease-out 0.15s forwards;
          opacity: 0;
        }
        .legal-subtitle {
          animation: heroFadeUp 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }

        .legal-gradient-text {
          background: linear-gradient(135deg, #ffffff 0%, #c96b66 55%, #b84e48 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .legal-content {
          background: #ffffff;
          border: 1px solid rgba(29,29,27,0.08);
          padding: 2.5rem;
        }
        @media (min-width: 640px) {
          .legal-content {
            padding: 3rem;
          }
        }

        .legal-section {
          border-left: 3px solid #b84e48;
          padding-left: 1.25rem;
          margin-top: 2rem;
        }

        .legal-section-title {
          font-size: 1rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #1d1d1b;
          margin-bottom: 0.75rem;
        }

        .legal-section-subtitle {
          font-size: 0.875rem;
          font-weight: 700;
          color: #1d1d1b;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .legal-section-subtitle::before {
          content: '';
          display: block;
          width: 0.5rem;
          height: 2px;
          background: #b84e48;
          flex-shrink: 0;
        }

        .legal-text {
          font-size: 0.9375rem;
          line-height: 1.75;
          color: rgba(29,29,27,0.7);
        }

        .legal-contact-card {
          background: rgba(29,29,27,0.03);
          border: 1px solid rgba(29,29,27,0.08);
          padding: 1.25rem 1.5rem;
          font-size: 0.9375rem;
          line-height: 1.75;
          color: rgba(29,29,27,0.75);
        }

        .legal-link {
          color: #b84e48;
          text-decoration: none;
          font-weight: 700;
          transition: color 0.15s ease;
        }
        .legal-link:hover {
          color: #c96b66;
          text-decoration: underline;
        }

        .legal-meta {
          margin-top: 2.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(29,29,27,0.08);
          font-size: 0.75rem;
          color: rgba(29,29,27,0.4);
        }
      `}</style>

      <div className="min-h-screen bg-paper">
        {/* Hero */}
        <div className="legal-hero-bg relative overflow-hidden px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div
            className="pointer-events-none absolute inset-0 texture-halftone"
            aria-hidden="true"
          />
          <div
            className="absolute left-0 bottom-0 top-0 w-1 bg-accent opacity-60"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
            aria-hidden="true"
          >
            <span className="select-none text-[clamp(80px,16vw,180px)] font-black uppercase tracking-widest text-white/[0.025] leading-none">
              LEGAL
            </span>
          </div>

          <div className="relative mx-auto max-w-3xl">
            <div className="legal-eyebrow mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-accent" aria-hidden="true" />
              <span className="text-xs font-black uppercase tracking-[0.3em] text-accent">
                Faninitiative Spandau e.V.
              </span>
            </div>
            <h1 className="legal-title text-[clamp(2.5rem,7vw,5rem)] font-black uppercase leading-[0.9] tracking-tight text-white">
              Impres-
              <br />
              <span className="legal-gradient-text">sum</span>
            </h1>
            <p className="legal-subtitle mt-5 text-sm text-white/45 sm:text-base">
              Angaben gemäß § 5 DDG
            </p>
          </div>
        </div>

        {/* Inhalt */}
        <div className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="legal-content">
              <div className="legal-section">
                <div className="legal-section-title">Verantwortliche Stelle</div>
                <div className="legal-contact-card">
                  Faninitiative Spandau e.V.
                  <br />
                  Kolonnenstr. 8
                  <br />
                  10827 Berlin
                </div>
              </div>

              <div className="legal-section">
                <div className="legal-section-title">Vertreten durch</div>
                <div className="legal-contact-card">
                  Glenn Odya
                  <br />
                  Christof Raatz
                </div>
              </div>

              <div className="legal-section">
                <div className="legal-section-title">Registereintrag</div>
                <div className="legal-contact-card">
                  Eintragung im Vereinsregister
                  <br />
                  Registergericht: Berlin
                  <br />
                  Registernummer: VR 41891
                </div>
              </div>

              <div className="legal-section">
                <div className="legal-section-title">Kontakt</div>
                <div className="legal-contact-card">
                  Telefon: 016090305525
                  <br />
                  E-Mail:{' '}
                  <a href="mailto:info@fanini.live" className="legal-link">
                    info@fanini.live
                  </a>
                </div>
              </div>

              <div className="legal-section">
                <div className="legal-section-title">Haftungsausschluss</div>

                <div className="legal-section-subtitle">Haftung für Inhalte</div>
                <p className="legal-text">
                  Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die
                  Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine
                  Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 DDG für eigene
                  Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8
                  bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte
                  oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu
                  forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                </p>

                <div className="legal-section-subtitle">Haftung für Links</div>
                <p className="legal-text">
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir
                  keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine
                  Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige
                  Anbieter oder Betreiber der Seiten verantwortlich.
                </p>

                <div className="legal-section-subtitle">Urheberrecht</div>
                <p className="legal-text">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
                  unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung,
                  Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes
                  bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
                  Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen
                  Gebrauch gestattet.
                </p>

                <div className="legal-section-subtitle">Datenschutz</div>
                <p className="legal-text">
                  Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten
                  möglich. Weitere Informationen findest du in unserer{' '}
                  <a href="/datenschutz" className="legal-link">
                    Datenschutzerklärung
                  </a>
                  .
                </p>
              </div>

              <div className="legal-meta">
                Impressum von{' '}
                <a
                  href="https://impressum-generator.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="legal-link"
                >
                  Impressum-Generator.de
                </a>
                . Powered by{' '}
                <a
                  href="https://www.kanzlei-hasselbach.de/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="legal-link"
                >
                  Franziska Hasselbach, Bonn
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
